import type { PropsWithChildren } from 'react'
import { createContext, useContext, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { GivingAction } from '@percent/partner-dashboard/services/givingActions/givingActionsService.types'
import { ArchiveGivingActionModal } from './ArchiveGivingAction/ArchiveGivingActionModal'
import { ArchiveGivingActionSuccessModal } from './ArchiveGivingAction/SuccessModal'
import { ArchiveGivingActionFailureModal } from './ArchiveGivingAction/FailureModal'

const ACTION_CREATE_ROUTE = '/workplace-giving/campaigns/actions/new'
const actionEditRoute = (actionId: string) => `/workplace-giving/campaigns/actions/${actionId}`

export type GivingActionsDialogsContextType = Readonly<{
  createGivingAction: () => void
  editGivingAction: (givingAction: GivingAction) => void
  archiveGivingAction: (givingAction: GivingAction) => void
}>

const GivingActionsDialogsContext = createContext<GivingActionsDialogsContextType | null>(null)

export type GivingActionsDialogsProviderProps = Readonly<{
  refresh: () => void
}>

export function GivingActionsDialogsProvider({
  children,
  refresh
}: PropsWithChildren<GivingActionsDialogsProviderProps>) {
  const navigate = useNavigate()

  const [archivingGivingAction, setArchivingGivingAction] = useState<GivingAction | null>(null)
  const [archiveSuccess, setArchiveSuccess] = useState(false)
  const [archiveError, setArchiveError] = useState(false)

  const onArchiveSuccess = () => {
    setArchivingGivingAction(null)
    setArchiveSuccess(true)
    refresh()
  }

  const onArchiveError = () => {
    setArchivingGivingAction(null)
    setArchiveError(true)
  }

  const value = useMemo(
    () => ({
      createGivingAction: () => navigate(ACTION_CREATE_ROUTE),
      editGivingAction: (givingAction: GivingAction) => navigate(actionEditRoute(givingAction.id)),
      archiveGivingAction: (givingAction: GivingAction) => setArchivingGivingAction(givingAction)
    }),
    [navigate]
  )

  return (
    <GivingActionsDialogsContext.Provider value={value}>
      {children}

      <ArchiveGivingActionModal
        isOpen={!!archivingGivingAction}
        handleClose={() => setArchivingGivingAction(null)}
        givingAction={archivingGivingAction}
        onSuccess={onArchiveSuccess}
        onError={onArchiveError}
      />
      <ArchiveGivingActionSuccessModal isOpen={archiveSuccess} handleClose={() => setArchiveSuccess(false)} />
      <ArchiveGivingActionFailureModal isOpen={archiveError} handleClose={() => setArchiveError(false)} />
    </GivingActionsDialogsContext.Provider>
  )
}

export const useGivingActionsDialogs = () => {
  const context = useContext(GivingActionsDialogsContext)

  if (!context) {
    throw new Error('useGivingActionsDialogs must be used within a GivingActionsDialogsProvider')
  }

  return context
}
